import { Component, Prop, h, Element, State, Host, Watch, Event, EventEmitter } from '@stencil/core';
import { FormValue, RowValue, Root } from './types';
import debounce from 'debounce';

@Component({
  tag: 'pn-dropdown-choice-adds-row',
  styleUrl: 'pn-dropdown-choice-adds-row.scss',
})
export class pnDropdownChoiceAddsRow {
  @Element() hostElement: Element;
  pnRowSelectDropdown: HTMLElement = null;
  tableHead: HTMLElement = null;
  tableBody: HTMLElement = null;
  private _dropdownData: Root[];

  @Prop() dropdownData: string | Root[] = null;
  @Prop() languageCode: string = null;
  @Prop() addRowDropdownName: string = '';
  @Prop() addRowDropdownPlaceholder: string = '';
  @Prop() addRowDropdownLabel: string = '';
  @Prop() rowNameLabel: string = '';
  @Prop() rowHasDropdown: boolean = true;
  @Prop() rowDropdownLabel: string = '';
  @Prop() rowDropdownPlaceholder: string = '';
  @Prop() rowInputLabel: string = '';
  @Prop() rowDeleteButtonText: string = '';
  @Prop() totalInputLimit: number = 0;
  @Prop() minimumInputLimit: number = 0;
  @Prop() inputLimitWarning: string = null;
  @Prop() inputEmptyWarning: string = null;
  @Prop() inputLowError: string = null;
  @Prop() dropdownEmptyWarning: string = null;

  @State() nextRowIndex: number = null;
  @State() rowSelectDropdownArr: string[] = null;
  @State() formValue: string = null;

  @Event() rowvalues: EventEmitter<string>;

  @Watch('dropdownData')
  dropdownDataWatcher() {
    if (typeof this.dropdownData === 'string') {
      this._dropdownData = JSON.parse(this.dropdownData);
    } else {
      this._dropdownData = this.dropdownData;
    }
  }

  componentWillLoad() {
    this.dropdownDataWatcher();
  }

  componentDidLoad() {
    this.setInitialValues();
    this.addEventHandlers();
  }

  addEventHandlers() {
    this.pnRowSelectDropdown.addEventListener('change', debounce(this.addRowElement.bind(this), 500));
  }

  setInitialValues() {
    this.nextRowIndex = 0;
    this.setRowSelectDropdownArr();
  }

  setRowSelectDropdownArr() {
    this.rowSelectDropdownArr = [];
    this._dropdownData.forEach(element => {
      this.rowSelectDropdownArr.push(element.label);
    });
  }

  formRowEvent = e => {
    const debouncedAddValue = debounce(this.updateHiddenFieldValue.bind(this), 500);

    if (e.target.matches('input')) {
      ['change', 'keyup'].forEach(eventName => {
        e.target.addEventListener(eventName, debouncedAddValue);
      });
    } else {
      const rowDropdown = e.target.closest('.generated-table-row')?.querySelector('pn-select') as HTMLElement;
      if (rowDropdown) {
        rowDropdown.addEventListener('change', debouncedAddValue);
      }
    }
  };

  updateHiddenFieldValue() {
    try {
      const allRows = [...this.tableBody.querySelectorAll('.generated-table-row')];
      const mainDropdown = this.hostElement.querySelector('.pn-row-select-dropdown');

      if (!mainDropdown) {
        return;
      }

      if (!allRows.length) {
        mainDropdown.setAttribute('error', `${this.dropdownEmptyWarning || "This field cannot be empty"}`);
        this.formValue = '';
        this.rowvalues.emit('');
        return;
      }

      let allRowsValid = true;
      const formValues = allRows.map(row => {
        const nameElm = row.querySelector('.tr-name');
        const dropdownElm = row.querySelector('.tr-dropdown');
        const inputElm = row.querySelector('.tr-input');

        if (!nameElm || !inputElm) {
          allRowsValid = false;
          return null;
        }

        const rowData = {
          row: [
            {
              name: this.rowNameLabel,
              value: nameElm.innerHTML
            }
          ]
        };

        if (dropdownElm && dropdownElm.querySelector('pn-select')) {
          const pnSelect = dropdownElm.querySelector('pn-select');
          const nativeSelect = pnSelect?.querySelector('select');
          const dropdownSelection = nativeSelect?.value || '';
          if (!dropdownSelection) {
            allRowsValid = false;
          } else {
            rowData.row.push({
              name: this.rowDropdownLabel,
              value: dropdownSelection
            });
          }
        }

        const input = inputElm.querySelector('input');
        const inputValue = parseInt(input?.value || '0', 10);
        if (inputValue > 0) {
          rowData.row.push({
            name: this.rowInputLabel,
            value: inputValue.toString()
          });
        } else {
          allRowsValid = false;
        }
        return rowData;
      });

      if (!allRowsValid || !formValues.length || formValues.includes(null)) {
        this.formValue = '';
        this.rowvalues.emit('');
        return;
      }

      this.formValue = `PN-JSON|${JSON.stringify(formValues)}`;
      
      if (this.validateInputs()) {
        this.rowvalues.emit(this.formValue);
      } else {
        this.rowvalues.emit('');
      }

    } catch (error) {
      console.error('Error updating field value: ', error);
      this.formValue = '';
    }
  }

  addRowElement(e: CustomEvent) {
    const selectedElement = e.target as HTMLSelectElement;
    const selectedValue = selectedElement?.value;

    if (!selectedValue) {
      return;
    }

    const mainDropdown = this.hostElement.querySelector('.pn-row-select-dropdown');
    mainDropdown?.removeAttribute('error');

    if (!this.tableBody) {
      console.error('tableBody is not defined.');
      return;
    }

    const newRow = document.createElement('div');
    newRow.setAttribute('index', `${this.nextRowIndex}`);
    newRow.className = 'generated-table-row';
    newRow.onclick = e => this.formRowEvent(e);

    const rowNameColumn = document.createElement('div');
    rowNameColumn.className = 'tr-name';
    rowNameColumn.innerHTML = selectedValue;
    rowNameColumn.dataset.name = selectedValue;
    newRow.appendChild(rowNameColumn);

    const selectedR = this._dropdownData?.find(item => item.label === selectedValue);

    if (selectedR?.values?.length) {
      this.rowHasDropdown = true;
      const dropdownCol = this.createDropdownColumn(selectedValue);
      newRow.appendChild(dropdownCol);
    } else {
      this.rowHasDropdown = false;
      const emptyDropdownCol = document.createElement('div');
      emptyDropdownCol.className = 'tr-column';
      newRow.appendChild(emptyDropdownCol);
    }

    const rowInputColumn = this.createInputColumn(selectedValue);
    newRow.appendChild(rowInputColumn);

    const deleteButton = this.createDeleteButtonColumn();
    newRow.appendChild(deleteButton);

    this.tableBody.appendChild(newRow);

    this.updateRowSelectDropdown();
    this.nextRowIndex++;
    selectedElement.value = '';
  }

  getCurrentRow(selectedValue: string) {
    return this.hostElement.querySelectorAll(`.tr-name[data-name='${selectedValue}'`);
  }

  createDropdownColumn(selectedValue: string) {
    const dropdownColumn = document.createElement('div');
    dropdownColumn.setAttribute('class', 'tr-dropdown');
    const selectedItem = this._dropdownData.find(arr => arr.label === selectedValue);
    const selectedOptionValues = [];
    const currentRow = this.getCurrentRow(selectedValue);

    currentRow.forEach(row => {
      selectedOptionValues.push(row.parentElement?.querySelector('.tr-dropdown [aria-selected=true]')?.textContent);
    });

    if (selectedItem.values.length) {
      const dropdown = document.createElement('pn-select');
      dropdown.setAttribute('name', `${selectedValue}-dropdown`);
      dropdown.setAttribute('placeholder', this.rowDropdownPlaceholder);
      dropdown.setAttribute('required', 'true');
      dropdown.setAttribute('error', '');

      const placeholder = document.createElement('option');
      placeholder.setAttribute("value", "");
      placeholder.textContent = this.rowDropdownPlaceholder;
      placeholder.selected = true;
      placeholder.disabled = true;
      dropdown.appendChild(placeholder);

      selectedItem.values.forEach(childItem => {
        const childOption = document.createElement('option');
        childOption.setAttribute("label", childItem.label);
        childOption.setAttribute("value", childItem.label);
        childOption.textContent = childItem.label;
        dropdown.appendChild(childOption);
      });

      dropdownColumn.appendChild(dropdown);
    }

    return dropdownColumn;
  }

  createInputColumn(selectedValue: string) {
    const inputColumn = document.createElement('div');
    inputColumn.setAttribute('class', 'tr-input');
    const inputElm = document.createElement('pn-input');
    inputElm.setAttribute('type', 'number');
    inputElm.setAttribute('name', `${selectedValue}-input`);
    if (this.totalInputLimit > 0) {
      inputElm.setAttribute('max', `${this.totalInputLimit}`);
    }
      inputElm.setAttribute('min', `${this.minimumInputLimit}`);
    inputElm.setAttribute('placeholder', `${this.minimumInputLimit}`);
    inputElm.setAttribute('error', '');
    inputColumn.appendChild(inputElm);
    return inputColumn;
  }

  createDeleteButtonColumn() {
    const buttonColumn = document.createElement('div');
    buttonColumn.setAttribute('class', 'tr-delete');
    const buttonElm = document.createElement('pn-button');
    buttonElm.onclick = e => this.deleteRow(e);
    buttonElm.setAttribute('appearance', 'warning');
    buttonElm.setAttribute('small', 'true');
    buttonElm.appendChild(document.createTextNode(this.rowDeleteButtonText));
    buttonColumn.appendChild(buttonElm);
    return buttonColumn;
  }

  updateRowSelectDropdown() {
    type Row = { rowName: string; selectedValue: string; placeholder: string };

    const rows: Row[] = [].map.call(this.tableBody.querySelectorAll('.tr-name'), el => {
      const dropdownColumn = el?.parentElement?.querySelector('.tr-dropdown');
      const dropdownPlaceholder = dropdownColumn?.querySelector('.pn-select-content span')?.textContent;
      const row: Row = {
        rowName: el?.innerHTML,
        selectedValue: dropdownColumn?.querySelector('[aria-selected=true]')?.textContent,
        placeholder: dropdownPlaceholder,
      };
      return row;
    });

    const selectOptions = this.pnRowSelectDropdown.querySelectorAll('option');
    selectOptions.forEach(elm => {
      elm.removeAttribute('selected');
    });
  }

  deleteRow(e: Event) {
    e.preventDefault();
    this.nextRowIndex--;
    const targetElm = e.currentTarget as HTMLButtonElement;
    const targetRow = targetElm.closest('.generated-table-row');
    targetRow.remove();
    const allRows = [...this.tableBody.querySelectorAll('.generated-table-row')];

    allRows.map((elm, index) => {
      elm.setAttribute('index', `${index}`);
    });
    this.updateHiddenFieldValue()
    this.updateRowSelectDropdown();
  }

  validateInputs() {
    const dropdownSelections = this.tableBody.querySelectorAll('pn-select');
    const numberInputs = this.tableBody.querySelectorAll('pn-input');
    let selectionEmpty = false;
    let showWarning = false;

    if (dropdownSelections) {
      dropdownSelections.forEach(dropdownElem => {
        if (!dropdownElem) {
          return;
        }

        const selection = dropdownElem.querySelector('select');
        const dropdownVal = selection?.value || '';

        if (dropdownVal === null || dropdownVal === "") {
          dropdownElem.setAttribute('error', `${this.dropdownEmptyWarning || "This field cannot be empty"}`);
          selectionEmpty = true;
        } else {
          dropdownElem.setAttribute('error', '');
        }
      });
    }

    if (numberInputs) {
      numberInputs.forEach(inputElem => {
        if (inputElem) {
          const currentNumber = inputElem.querySelector("input");
          let currentValue = parseInt(currentNumber.value);

          if (this.totalInputLimit < currentValue && inputElem.hasAttribute('max')) {
            inputElem.setAttribute('error', `${this.inputLimitWarning || "Amount too high"}`);
            showWarning = true;
          } else if (currentValue <= 0 || isNaN(currentValue)) {
            inputElem.setAttribute('error', `${this.inputEmptyWarning || "This field cannot be empty"}`);
            showWarning = true;
          } else {
            inputElem.setAttribute('error', '');
            showWarning = false;
          }

          if (this.minimumInputLimit > currentValue && inputElem.hasAttribute('min')) {
            inputElem.setAttribute('error', `${this.inputLowError || "Amount too low"}`);
            showWarning = true;
          } else if (currentValue <= 0 || isNaN(currentValue)) {
            inputElem.setAttribute('error', `${this.inputEmptyWarning || "This field cannot be empty"}`);
            showWarning = true;
          }
          else {
            inputElem.setAttribute('error', '');
            showWarning = false;
          }
        }
      });
    }

    if (selectionEmpty || showWarning) {
      return false;
    }

    return true;
  }

  render() {
    return (
      <Host>
        <div class="dropdown-choice-form-row">
          <pn-select
            ref={el => (this.pnRowSelectDropdown = el as HTMLElement)}
            name={this.addRowDropdownName}
            label={this.addRowDropdownLabel}
            class="pn-row-select-dropdown"
            language={this.languageCode}>
            <option value="" selected disabled>{this.addRowDropdownPlaceholder}</option>
            {this.rowSelectDropdownArr?.map((value: string) => <option label={value} value={value}>{value}</option>)}
          </pn-select>
        </div>
        <div class="dropdown-choice-form-row">
          <div class="pn-dropdown-choice-table">
            <div ref={el => (this.tableHead = el as HTMLElement)}>
              {this.nextRowIndex === 0 ? null : (
                <div class="table-header">
                  <div class="table-header-name">
                    <label>{this.rowNameLabel}</label>
                  </div>
                  <div class="table-header-dropdown">
                    <label>{this.rowDropdownLabel}</label>
                  </div>
                  <div class="table-header-input">
                    <label>{this.rowInputLabel}</label>
                  </div>
                  <div class="table-header-delete"></div>
                </div>
              )}
            </div>
            <div ref={el => (this.tableBody = el as HTMLElement)} class="pn-dropdown-choice-table-slot">
              <slot></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
